import * as z$1 from "zod/v4";
//#region src/agent-execution-readiness-impact-taxonomy.ts
const AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS = 1800 * 1e3;
const AGENT_EXECUTION_READINESS_IMPACT_REASON_CODES = [
	"controller.launch.choice_required",
	"controller.launch.needs_repair",
	"controller.client.upgrade_required",
	"controller.runtime.missing",
	"runtime_coordinator.delivery_expired",
	"workspace.invalid",
	"workspace.missing"
];
const AGENT_EXECUTION_READINESS_IMPACT_KINDS = ["local_execution"];
const AGENT_EXECUTION_READINESS_IMPACT_SOURCES = ["wake_failure"];
const AGENT_EXECUTION_READINESS_IMPACT_EFFECTS = ["current_readiness_blocker"];
const AGENT_EXECUTION_READINESS_IMPACT_SCOPES = ["agent_profile_binding_agent_controller_report"];
const AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS = [
	"fresh_ttl_expires",
	"matching_execution_running",
	"matching_execution_success"
];
const AGENT_EXECUTION_READINESS_IMPACT_REASON_CODE_ALIASES = {
	"project.local_connection.folder_missing": "workspace.missing",
	"project.local_connection.missing": "workspace.missing",
	"profile.workspace.invalid": "workspace.invalid",
	"profile.workspace.missing": "workspace.missing"
};
const AGENT_EXECUTION_READINESS_IMPACT_TAXONOMY = [
	{
		clearingTriggers: AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS,
		effect: "current_readiness_blocker",
		freshTtlMs: AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS,
		impactKind: "local_execution",
		reasonCode: "controller.launch.choice_required",
		scope: "agent_profile_binding_agent_controller_report",
		source: "wake_failure"
	},
	{
		clearingTriggers: AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS,
		effect: "current_readiness_blocker",
		freshTtlMs: AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS,
		impactKind: "local_execution",
		reasonCode: "controller.launch.needs_repair",
		scope: "agent_profile_binding_agent_controller_report",
		source: "wake_failure"
	},
	{
		clearingTriggers: AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS,
		effect: "current_readiness_blocker",
		freshTtlMs: AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS,
		impactKind: "local_execution",
		reasonCode: "controller.client.upgrade_required",
		scope: "agent_profile_binding_agent_controller_report",
		source: "wake_failure"
	},
	{
		clearingTriggers: AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS,
		effect: "current_readiness_blocker",
		freshTtlMs: AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS,
		impactKind: "local_execution",
		reasonCode: "controller.runtime.missing",
		scope: "agent_profile_binding_agent_controller_report",
		source: "wake_failure"
	},
	{
		clearingTriggers: AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS,
		effect: "current_readiness_blocker",
		freshTtlMs: AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS,
		impactKind: "local_execution",
		reasonCode: "runtime_coordinator.delivery_expired",
		scope: "agent_profile_binding_agent_controller_report",
		source: "wake_failure"
	},
	{
		clearingTriggers: AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS,
		effect: "current_readiness_blocker",
		freshTtlMs: AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS,
		impactKind: "local_execution",
		reasonCode: "workspace.invalid",
		scope: "agent_profile_binding_agent_controller_report",
		source: "wake_failure"
	},
	{
		clearingTriggers: AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS,
		effect: "current_readiness_blocker",
		freshTtlMs: AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS,
		impactKind: "local_execution",
		reasonCode: "workspace.missing",
		scope: "agent_profile_binding_agent_controller_report",
		source: "wake_failure"
	}
];
const KNOWN_FINAL_FAILURE_REASON_CODES_WITHOUT_READINESS_IMPACT = [
	"openmeld.space_action.repair_failed",
	"daemon.delivery.execution_stalled",
	"daemon.delivery.finalization_timeout",
	"daemon.delivery.start_timeout",
	"daemon.delivery.timeout",
	"delivery.result_timeout",
	"delivery.start_timeout",
	"final_visible_outcome.publication_pending",
	"final_visible_outcome.publication_retrying",
	"final_visible_outcome.publication_unresolved",
	"local_adapter.timeout",
	"reply.invalid_space_action",
	"reply.publication_failed",
	"runtime.progress_stale",
	"runtime.reply.empty_output",
	"runtime.reply.metadata_missing.agent_controller_ref",
	"runtime_coordinator.failure_publication_failed",
	"runtime_coordinator.failure_publication_unavailable",
	"runtime_coordinator.publication_retry_exhausted",
	"runtime_coordinator.publication_unresolved",
	"space.signal.persist_failed",
	"timed_out_after_output",
	"timed_out_no_output",
	"timeout"
];
const agentExecutionReadinessImpactReasonCodeSchema = z$1.enum(AGENT_EXECUTION_READINESS_IMPACT_REASON_CODES);
const agentExecutionReadinessImpactKindSchema = z$1.enum(AGENT_EXECUTION_READINESS_IMPACT_KINDS);
const agentExecutionReadinessImpactSourceSchema = z$1.enum(AGENT_EXECUTION_READINESS_IMPACT_SOURCES);
const agentExecutionReadinessImpactEffectSchema = z$1.enum(AGENT_EXECUTION_READINESS_IMPACT_EFFECTS);
const agentExecutionReadinessImpactScopeSchema = z$1.enum(AGENT_EXECUTION_READINESS_IMPACT_SCOPES);
const agentExecutionReadinessImpactClearingTriggerSchema = z$1.enum(AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS);
function resolveAgentExecutionReadinessImpactTaxonomyEntry(reasonCode) {
	const resolvedReasonCode = resolveAgentExecutionReadinessImpactReasonCode(reasonCode);
	if (!resolvedReasonCode) return null;
	return AGENT_EXECUTION_READINESS_IMPACT_TAXONOMY.find((entry) => entry.reasonCode === resolvedReasonCode) ?? null;
}
function resolveAgentExecutionReadinessImpactReasonCode(reasonCode) {
	if (typeof reasonCode !== "string") return null;
	const normalized = reasonCode.trim().toLowerCase();
	if (!normalized) return null;
	const alias = AGENT_EXECUTION_READINESS_IMPACT_REASON_CODE_ALIASES[normalized];
	if (alias) return alias;
	return AGENT_EXECUTION_READINESS_IMPACT_REASON_CODES.includes(normalized) ? normalized : null;
}
function isAgentExecutionReadinessImpactReasonCode(reasonCode) {
	return resolveAgentExecutionReadinessImpactReasonCode(reasonCode) !== null;
}
function isKnownFinalFailureReasonCodeWithoutReadinessImpact(reasonCode) {
	return typeof reasonCode === "string" && KNOWN_FINAL_FAILURE_REASON_CODES_WITHOUT_READINESS_IMPACT.includes(reasonCode);
}
//#endregion
export { AGENT_EXECUTION_READINESS_IMPACT_CLEARING_TRIGGERS, AGENT_EXECUTION_READINESS_IMPACT_EFFECTS, AGENT_EXECUTION_READINESS_IMPACT_FRESH_TTL_MS, AGENT_EXECUTION_READINESS_IMPACT_KINDS, AGENT_EXECUTION_READINESS_IMPACT_REASON_CODES, AGENT_EXECUTION_READINESS_IMPACT_REASON_CODE_ALIASES, AGENT_EXECUTION_READINESS_IMPACT_SCOPES, AGENT_EXECUTION_READINESS_IMPACT_SOURCES, AGENT_EXECUTION_READINESS_IMPACT_TAXONOMY, KNOWN_FINAL_FAILURE_REASON_CODES_WITHOUT_READINESS_IMPACT, agentExecutionReadinessImpactClearingTriggerSchema, agentExecutionReadinessImpactEffectSchema, agentExecutionReadinessImpactKindSchema, agentExecutionReadinessImpactReasonCodeSchema, agentExecutionReadinessImpactScopeSchema, agentExecutionReadinessImpactSourceSchema, isAgentExecutionReadinessImpactReasonCode, isKnownFinalFailureReasonCodeWithoutReadinessImpact, resolveAgentExecutionReadinessImpactReasonCode, resolveAgentExecutionReadinessImpactTaxonomyEntry };

//# sourceMappingURL=agent-execution-readiness-impact-taxonomy.js.map